import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser, useOrganization } from "@clerk/clerk-react";
import { useApiRequest, useAuthenticatedQuery } from "@/hooks/use-api";
import { OrderTemplate, InsertOrderTemplate } from "@shared/schema";

export function useOrderTemplates() {
  const { user } = useUser();
  const { organization } = useOrganization();

  // Gated on Clerk having loaded the user and active org so the request
  // never fires before the session can authorize it. Callers should use
  // `isPending` (not `isLoading`) as their loading flag — it stays true
  // while the query is disabled during Clerk initialization.
  return useAuthenticatedQuery<OrderTemplate[]>(
    ["/api/order-templates", organization?.id],
    { enabled: !!user && !!organization },
  );
}

export function useCreateOrderTemplate() {
  const queryClient = useQueryClient();
  const { apiRequest } = useApiRequest();

  return useMutation({
    mutationFn: async (
      templateData: InsertOrderTemplate,
    ): Promise<OrderTemplate> => {
      const response = await apiRequest(
        "POST",
        "/api/order-templates",
        templateData,
      );
      if (!response.ok) {
        throw new Error(`Failed to create order template: ${response.status}`);
      }
      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch order templates
      queryClient.invalidateQueries({ queryKey: ["/api/order-templates"] });
    },
  });
}

export function useUpdateOrderTemplate() {
  const queryClient = useQueryClient();
  const { apiRequest } = useApiRequest();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<OrderTemplate>;
    }): Promise<OrderTemplate> => {
      const response = await apiRequest(
        "PATCH",
        `/api/order-templates/${id}`,
        data,
      );
      if (!response.ok) {
        throw new Error(`Failed to update order template: ${response.status}`);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/order-templates"] });
    },
  });
}

export function useDeleteOrderTemplate() {
  const queryClient = useQueryClient();
  const { apiRequest } = useApiRequest();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const response = await apiRequest("DELETE", `/api/order-templates/${id}`);
      if (!response.ok) {
        throw new Error(`Failed to delete order template: ${response.status}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/order-templates"] });
    },
  });
}

export function useIncrementTemplateUsage() {
  const { apiRequest } = useApiRequest();

  return useMutation({
    mutationFn: async (templateId: string): Promise<void> => {
      const response = await apiRequest(
        "PATCH",
        `/api/order-templates/${templateId}/increment-usage`,
        {},
      );
      if (!response.ok) {
        throw new Error(
          `Failed to increment template usage: ${response.status}`,
        );
      }
    },
    // Don't invalidate queries - usage count updates aren't critical for UI
    onError: (error) => {
      console.warn("Failed to increment template usage:", error);
    },
  });
}
